import React, { useRef } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const ListOfTeacherReport = ({ 
  isOpen, 
  onClose, 
  selectedCampusId,
  selectedClassId,
  selectedSectionId,
  campusName,
  className,
  sectionName
}) => {
  const reportRef = useRef();
  const { teacherReport, loading, error } = useSelector((state) => state.teacherReport);

  // Use fetched teacher data from Redux, fallback to empty array if not available
  const teachers = teacherReport || [];

  // Dummy teacher data - kept as fallback (can be removed once API is confirmed working)
  const dummyTeachers = [
    {
      id: "TCH001",
      name: "Dr. Sarah Khan",
      dateOfBirth: "15/03/1985",
      qualification: "PhD in Mathematics, M.Ed",
      subjectsTaught: ["Mathematics", "Statistics"],
      gradesAssigned: ["Grade 8", "Grade 9", "Grade 10"],
      joiningDate: "01/09/2020",
      experience: "8 years",
      contactInfo: "+92-300-1234567, sarah.khan@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH002",
      name: "Mr. Ali Hassan",
      dateOfBirth: "22/07/1988",
      qualification: "M.Sc Physics, B.Ed",
      subjectsTaught: ["Physics", "Chemistry"],
      gradesAssigned: ["Grade 9", "Grade 10", "Grade 11"],
      joiningDate: "15/08/2019",
      experience: "6 years",
      contactInfo: "+92-301-2345678, ali.hassan@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH003",
      name: "Ms. Fatima Ahmed",
      dateOfBirth: "08/11/1990",
      qualification: "M.A English Literature, B.Ed",
      subjectsTaught: ["English", "Literature"],
      gradesAssigned: ["Grade 6", "Grade 7", "Grade 8"],
      joiningDate: "01/09/2021",
      experience: "4 years",
      contactInfo: "+92-302-3456789, fatima.ahmed@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH004",
      name: "Dr. Omar Sheikh",
      dateOfBirth: "14/05/1982",
      qualification: "PhD in Biology, M.Ed",
      subjectsTaught: ["Biology", "Environmental Science"],
      gradesAssigned: ["Grade 10", "Grade 11", "Grade 12"],
      joiningDate: "01/09/2018",
      experience: "10 years",
      contactInfo: "+92-303-4567890, omar.sheikh@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH005",
      name: "Ms. Aisha Malik",
      dateOfBirth: "03/12/1987",
      qualification: "M.A Urdu, B.Ed",
      subjectsTaught: ["Urdu", "Islamic Studies"],
      gradesAssigned: ["Grade 6", "Grade 7", "Grade 8"],
      joiningDate: "20/09/2020",
      experience: "5 years",
      contactInfo: "+92-304-5678901, aisha.malik@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH006",
      name: "Mr. Hassan Raza",
      dateOfBirth: "19/09/1984",
      qualification: "M.Sc Computer Science, B.Ed",
      subjectsTaught: ["Computer Science", "ICT"],
      gradesAssigned: ["Grade 8", "Grade 9", "Grade 10"],
      joiningDate: "01/09/2019",
      experience: "7 years",
      contactInfo: "+92-305-6789012, hassan.raza@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH007",
      name: "Ms. Zainab Ahmed",
      dateOfBirth: "27/01/1989",
      qualification: "M.A History, B.Ed",
      subjectsTaught: ["History", "Geography", "Social Studies"],
      gradesAssigned: ["Grade 6", "Grade 7", "Grade 8"],
      joiningDate: "10/09/2021",
      experience: "4 years",
      contactInfo: "+92-306-7890123, zainab.ahmed@school.edu.pk",
      employmentStatus: "Active"
    },
    {
      id: "TCH008",
      name: "Dr. Usman Ali",
      dateOfBirth: "11/06/1981",
      qualification: "PhD in Chemistry, M.Ed",
      subjectsTaught: ["Chemistry", "Advanced Chemistry"],
      gradesAssigned: ["Grade 11", "Grade 12"],
      joiningDate: "01/09/2017",
      experience: "11 years",
      contactInfo: "+92-307-8901234, usman.ali@school.edu.pk",
      employmentStatus: "Active"
    }
  ];

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "List_of_Teachers_Report.pdf", 2);
  };

  const downloadExcel = () => {
    if (!teachers || teachers.length === 0) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["List of Teachers Report Summary"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      ["Total Teachers", teachers.length],
      [],
      ["Teacher ID", "Name", "Date of Birth", "Qualification", "Subjects Taught", "Grades Assigned", "Joining Date", "Experience", "Contact Information", "Employment Status"]
    ];

    // Add teacher data
    teachers.forEach(teacher => {
      summaryData.push([
        teacher.id || teacher.teacherId || teacher.employeeId || 'N/A',
        teacher.name || teacher.fullName || teacher.teacherName || 'N/A',
        teacher.dateOfBirth || teacher.dob || teacher.birthDate || 'N/A',
        teacher.qualification || teacher.qualifications || 'N/A',
        Array.isArray(teacher.subjectsTaught) 
          ? teacher.subjectsTaught.join(", ") 
          : teacher.subjects || teacher.subjectNames || 'N/A',
        Array.isArray(teacher.gradesAssigned) 
          ? teacher.gradesAssigned.join(", ") 
          : teacher.grades || teacher.classNames || 'N/A',
        teacher.joiningDate || teacher.hireDate || teacher.startDate || 'N/A',
        teacher.experience || teacher.yearsOfExperience || 'N/A',
        teacher.contactInfo || teacher.contact || teacher.phone || teacher.email || 'N/A',
        teacher.employmentStatus || teacher.status || 'Active'
      ]);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Teacher List");

    // Individual teacher details sheets
    teachers.forEach((teacher, index) => {
      const teacherName = teacher.name || teacher.fullName || teacher.teacherName || `Teacher ${index + 1}`;
      const teacherData = [
        ["Teacher Details"],
        ["Teacher ID", teacher.id || teacher.teacherId || teacher.employeeId || 'N/A'],
        ["Name", teacherName],
        ["Date of Birth", teacher.dateOfBirth || teacher.dob || teacher.birthDate || 'N/A'],
        ["Qualification", teacher.qualification || teacher.qualifications || 'N/A'],
        ["Subjects Taught", Array.isArray(teacher.subjectsTaught) 
          ? teacher.subjectsTaught.join(", ") 
          : teacher.subjects || teacher.subjectNames || 'N/A'],
        ["Grades Assigned", Array.isArray(teacher.gradesAssigned) 
          ? teacher.gradesAssigned.join(", ") 
          : teacher.grades || teacher.classNames || 'N/A'],
        ["Joining Date", teacher.joiningDate || teacher.hireDate || teacher.startDate || 'N/A'],
        ["Experience", teacher.experience || teacher.yearsOfExperience || 'N/A'],
        ["Contact Information", teacher.contactInfo || teacher.contact || teacher.phone || teacher.email || 'N/A'],
        ["Employment Status", teacher.employmentStatus || teacher.status || 'Active'],
        ["Campus", campusName],
        ["Report Generated", new Date().toLocaleDateString('en-GB')]
      ];

      const teacherSheet = XLSX.utils.aoa_to_sheet(teacherData);
      const sheetName = teacherName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, teacherSheet, sheetName);
    });

    XLSX.writeFile(workbook, "List_of_Teachers_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        
        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-lg">Loading teacher report...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-8">
            <p className="text-red-500 text-lg">Error: {error}</p>
            <button 
              onClick={onClose} 
              className="mt-4 bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {/* Report Content */}
        {!loading && !error && (
          <>
            {/* Report Header */}
            <div className="text-center mb-8 border-b pb-4">
              <h1 className="text-2xl font-bold text-[#241763] mb-2">List of Teachers Report</h1>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-left">
                  <p><strong>Campus:</strong> {campusName}</p>
                  <p><strong>Class:</strong> {className}</p>
                </div>
                <div className="text-right">
                  <p><strong>Section:</strong> {sectionName}</p>
                  <p><strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}</p>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                <strong>Total Teachers:</strong> {teachers.length}
              </p>
            </div>

            {/* Individual Teacher Pages */}
            {teachers && teachers.length > 0 ? (
              teachers.map((teacher, index) => {
                const teacherId = teacher.id || teacher.teacherId || teacher.employeeId || index;
                return (
            <div key={teacherId} style={{ pageBreakAfter: index < teachers.length - 1 ? 'always' : 'auto' }}>
              {/* Individual Teacher Report */}
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                  <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
                  <h2 className="text-lg font-semibold mt-2">Teacher Information Report</h2>
                  <p className="text-base">{new Date().getFullYear()}</p>
                </div>

                {/* Teacher Info */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Teacher ID</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{teacher.id || teacher.teacherId || teacher.employeeId || 'N/A'}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Name</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{teacher.name || teacher.fullName || teacher.teacherName || 'N/A'}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Date of Birth</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{teacher.dateOfBirth || teacher.dob || teacher.birthDate || 'N/A'}</p>
                      <p className="w-16 font-semibold ml-8">Status</p>
                      <p className="w-32 border-b border-gray-400 ml-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (teacher.employmentStatus || teacher.status || 'active')?.toLowerCase() === "active" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-red-100 text-red-800"
                        }`}>
                          {teacher.employmentStatus || teacher.status || 'Active'}
                        </span>
                      </p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Qualification</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{teacher.qualification || teacher.qualifications || 'N/A'}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Joining Date</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{teacher.joiningDate || teacher.hireDate || teacher.startDate || 'N/A'}</p>
                      <p className="w-16 font-semibold ml-8">Experience</p>
                      <p className="w-32 border-b border-gray-400 ml-4">{teacher.experience || teacher.yearsOfExperience || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Teaching Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#241763]">Teaching Information</h3>
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Subjects Taught</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">
                        {Array.isArray(teacher.subjectsTaught) 
                          ? teacher.subjectsTaught.join(", ") 
                          : teacher.subjects || teacher.subjectNames || 'N/A'}
                      </p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Grades Assigned</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">
                        {Array.isArray(teacher.gradesAssigned) 
                          ? teacher.gradesAssigned.join(", ") 
                          : teacher.grades || teacher.classNames || 'N/A'}
                      </p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Contact Info</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">
                        {teacher.contactInfo || teacher.contact || teacher.phone || teacher.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#241763]">Additional Information</h3>
                  <div className="grid grid-cols-1 gap-y-2">
                    <div className="flex">
                      <p className="w-32 font-semibold">Campus</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">{campusName}</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Academic Year</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">2023-24</p>
                    </div>
                    <div className="flex">
                      <p className="w-32 font-semibold">Report Type</p>
                      <p className="flex-1 border-b border-gray-400 ml-4">Teacher Information Report</p>
                    </div>
                  </div>
                </div>

                {/* Teaching Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#241763]">Teaching Summary</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600">Subjects</div>
                      <div className="text-lg font-bold text-blue-600">
                        {Array.isArray(teacher.subjectsTaught) 
                          ? teacher.subjectsTaught.length 
                          : Array.isArray(teacher.subjects) 
                            ? teacher.subjects.length 
                            : 0}
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600">Grades</div>
                      <div className="text-lg font-bold text-green-600">
                        {Array.isArray(teacher.gradesAssigned) 
                          ? teacher.gradesAssigned.length 
                          : Array.isArray(teacher.grades) 
                            ? teacher.grades.length 
                            : 0}
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600">Experience</div>
                      <div className="text-lg font-bold text-purple-600">{teacher.experience || teacher.yearsOfExperience || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Signature Lines */}
                <div className="grid grid-cols-3 gap-8 mt-12">
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Teacher's Signature
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Headmistress's Signature
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-gray-400 mt-8 pt-2">
                      Principal's Signature
                    </div>
                  </div>
                </div>
              </div>
            </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">No teachers found for the selected criteria.</p>
              </div>
            )}

            {/* Download Buttons */}
            <div className="no-print flex justify-end mt-6 space-x-4">
          <button 
            onClick={downloadPDF} 
            className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as PDF
          </button>
          <button 
            onClick={downloadExcel} 
            className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as Excel
          </button>
          <button 
            onClick={onClose} 
            className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
          >
              Close
            </button>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ListOfTeacherReport;

